import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import DriverBottomNav from "@/components/DriverBottomNav";
import FranchiseAdminPanel from "@/components/FranchiseAdminPanel";
import SupportTicketDialog from "@/components/SupportTicketDialog";
import TenantTrialBanner from "@/components/TenantTrialBanner";
import FranchiseTicketsInbox from "@/components/FranchiseTicketsInbox";
import AdminUserFix from "@/components/AdminUserFix";
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  DollarSign, 
  Tag, 
  Settings,
  Package,
  Phone,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  MapPin,
  Car,
  FileImage,
  Crown,
  Radio,
  Percent,
  FileText,
  Download,
  KeyRound,
  Truck,
  Building2,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

import { API } from "@/lib/api";
import { geocodeAddress } from "@/lib/geo";
import CustomerLookupCard from "@/components/CustomerLookupCard";
import DeleteCustomerDialog from "@/components/DeleteCustomerDialog";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import BulkPasteStripeCard from "@/components/BulkPasteStripeCard";


const DAYS_OF_WEEK = [
  { id: "monday", label: "Mon" },
  { id: "tuesday", label: "Tue" },
  { id: "wednesday", label: "Wed" },
  { id: "thursday", label: "Thu" },
  { id: "friday", label: "Fri" },
  { id: "saturday", label: "Sat" },
  { id: "sunday", label: "Sun" }
];

export default function Admin() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "pricing";
    const t = new URLSearchParams(window.location.search).get("tab");
    return t || "pricing";
  });
  const [services, setServices] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [config, setConfig] = useState({
    supportPhone: "1 (484) 577-3677",
    businessName: "Link Ride",
    businessEIN: "",
    dispatchMode: "manual",
    dispatchWindowSeconds: 30,
    salesTaxPct: 6.0,
    mercantileTaxPct: 0.075,
    platformCommissionPct: 20.0,
    geoAnchorLat: 40.3337,
    geoAnchorLng: -75.9474,
    geoAutocompleteBiasMiles: 60,
    irsStandardMileageRate: 0.70,
  });
  const [drivers, setDrivers] = useState([]);
  const [driverFilter, setDriverFilter] = useState("pending");
  const [plans, setPlans] = useState([]);
  const [taxReport, setTaxReport] = useState(null);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  // Customers directory (lazy-loaded when the Customers tab is opened)
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  // When the admin taps a row in "All customers", we pipe the email into
  // CustomerLookupCard so it auto-searches + auto-selects without the admin
  // having to retype anything. Bumped via a counter so re-clicking the same
  // user re-fires the effect even when the email hasn't changed.
  const [lookupInitialEmail, setLookupInitialEmail] = useState("");
  const [lookupBump, setLookupBump] = useState(0);
  // Test-customer wipe state
  const [wipePreview, setWipePreview] = useState(null); // { matched, would_delete, skipped_real_users, preview, skipped }
  const [wipeLoading, setWipeLoading] = useState(false);
  const [wipeCommitting, setWipeCommitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [driverActionId, setDriverActionId] = useState(null);
  // Rejection workflow
  const [rejectTarget, setRejectTarget] = useState(null); // { id, name }
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [signupCaps, setSignupCaps] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, email, ...} | null
  const REJECTION_PRESETS = [
    "Insurance does not meet rideshare endorsement requirement",
    "Vehicle year is older than 2016",
    "Driver's license documents incomplete or unreadable",
    "Background check returned issues",
    "Vehicle inspection / registration missing",
    "Other (see notes)",
  ];

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, specialsRes, configRes, driversRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/services`),
        axios.get(`${API}/admin/specials`),
        axios.get(`${API}/admin/config`).catch(() => ({ data: { supportPhone: "1 (484) 577-3677", dispatchMode: "manual", dispatchWindowSeconds: 30 } })),
        axios.get(`${API}/admin/drivers`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/plans`).catch(() => ({ data: [] }))
      ]);
      setServices(servicesRes.data);
      setSpecials(specialsRes.data);
      setConfig({
        supportPhone: configRes.data.supportPhone || "1 (484) 577-3677",
        businessName: configRes.data.businessName || "Link Ride",
        businessEIN: configRes.data.businessEIN || "",
        dispatchMode: configRes.data.dispatchMode || "manual",
        dispatchWindowSeconds: Number(configRes.data.dispatchWindowSeconds || 30),
        salesTaxPct: Number(configRes.data.salesTaxPct ?? 6.0),
        mercantileTaxPct: Number(configRes.data.mercantileTaxPct ?? 0.075),
        platformCommissionPct: Number(configRes.data.platformCommissionPct ?? 20.0),
        geoAnchorLat: Number(configRes.data.geoAnchorLat ?? 40.3337),
        geoAnchorLng: Number(configRes.data.geoAnchorLng ?? -75.9474),
        geoAutocompleteBiasMiles: Number(configRes.data.geoAutocompleteBiasMiles ?? 60),
        irsStandardMileageRate: Number(configRes.data.irsStandardMileageRate ?? 0.70),
      });
      setDrivers(driversRes.data || []);
      setPlans(plansRes.data || []);
      // Fire-and-forget — Beta capacity gauges (not critical to load)
      axios.get(`${API}/admin/signup-caps`)
        .then((r) => setSignupCaps(r.data))
        .catch(() => {});
    } catch (e) {
      console.error("Failed to fetch admin data:", e);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin) return;
    fetchData();
  }, [authLoading, isAuthenticated, isAdmin, fetchData]);

  // Redirect non-admins to login
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?redirect=/driver/admin');
    } else if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  // Lazy-load the customer directory only when the tab is opened
  const fetchCustomers = useCallback(async (search) => {
    setCustomersLoading(true);
    try {
      const params = {};
      if (search && search.trim()) params.q = search.trim();
      const { data } = await axios.get(`${API}/admin/customers`, { params });
      setCustomers(data?.customers || []);
    } catch (e) {
      toast.error("Failed to load customers");
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "customers") return;
    if (authLoading || !isAdmin) return;
    fetchCustomers("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, authLoading, isAdmin]);

  // Preview the test-customer wipe (safe — no deletes yet).
  const previewWipeTestCustomers = useCallback(async () => {
    setWipeLoading(true);
    try {
      const { data } = await axios.delete(`${API}/admin/customers`, {
        params: { test_only: true, dry_run: true },
      });
      setWipePreview(data);
      if ((data?.would_delete || 0) === 0) {
        toast.info(`No test customers found. Skipped ${data?.skipped_real_users || 0} real users with credits / bookings / subscriptions.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not preview test customers");
    } finally {
      setWipeLoading(false);
    }
  }, []);

  // Commit the wipe — only after preview was shown.
  const commitWipeTestCustomers = useCallback(async () => {
    if (!wipePreview?.would_delete) return;
    setWipeCommitting(true);
    try {
      const { data } = await axios.delete(`${API}/admin/customers`, {
        params: { test_only: true, dry_run: false },
      });
      toast.success(`Deleted ${data.deleted} test customer${data.deleted === 1 ? '' : 's'}. ${data.skipped_real_users} real users skipped.`);
      setWipePreview(null);
      fetchCustomers(customerSearch);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete test customers");
    } finally {
      setWipeCommitting(false);
    }
  }, [wipePreview, fetchCustomers, customerSearch]);

  // Service handlers
  const updateService = (id, field, value) => {
    setServices(prev => prev.map(s => 
      s.id === id ? { ...s, [field]: ['basePrice','perMile','minPrice'].includes(field) ? parseFloat(value) || 0 : value } : s
    ));
  };

  const saveServices = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/services`, { services });
      toast.success("Pricing saved!");
    } catch (e) {
      toast.error("Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  // Specials handlers
  const updateSpecial = (id, field, value) => {
    setSpecials(prev => prev.map(s => 
      s.id === id ? { ...s, [field]: field === 'discount' ? parseInt(value) || 0 : value } : s
    ));
  };

  const toggleSpecialDay = (id, day) => {
    setSpecials(prev => prev.map(s => {
      if (s.id === id) {
        const days = s.days.includes(day) 
          ? s.days.filter(d => d !== day)
          : [...s.days, day];
        return { ...s, days };
      }
      return s;
    }));
  };

  const addSpecial = () => {
    const newSpecial = {
      id: `special-${Date.now()}`,
      city: "New City",
      state: "TX",
      days: ["monday"],
      discount: 10,
      description: "New City Special: 10% off"
    };
    setSpecials(prev => [...prev, newSpecial]);
  };

  const removeSpecial = (id) => {
    setSpecials(prev => prev.filter(s => s.id !== id));
  };

  const saveSpecials = async () => {
    setSaving(true);
    try {
      // Update descriptions based on city, discount, and days
      const updatedSpecials = specials.map(s => ({
        ...s,
        description: `${s.city} Special: ${s.discount}% off ${s.days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}`
      }));
      await axios.put(`${API}/admin/specials`, { specials: updatedSpecials });
      setSpecials(updatedSpecials);
      toast.success("Specials saved!");
    } catch (e) {
      toast.error("Failed to save specials");
    } finally {
      setSaving(false);
    }
  };

  // Config handlers
  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, {
        supportPhone: config.supportPhone,
        businessName: config.businessName || "Link Ride",
        businessEIN: config.businessEIN || "",
        dispatchMode: config.dispatchMode,
        dispatchWindowSeconds: Number(config.dispatchWindowSeconds) || 30,
        salesTaxPct: Number(config.salesTaxPct) || 0,
        mercantileTaxPct: Number(config.mercantileTaxPct) || 0,
        platformCommissionPct: Number(config.platformCommissionPct) || 0,
        geoAnchorLat: Number(config.geoAnchorLat) || 40.3337,
        geoAnchorLng: Number(config.geoAnchorLng) || -75.9474,
        geoAutocompleteBiasMiles: Number(config.geoAutocompleteBiasMiles) || 60,
        irsStandardMileageRate: Number(config.irsStandardMileageRate) || 0.70,
      });
      toast.success("Settings saved!");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Tax report
  const loadTaxReport = useCallback(async (year) => {
    try {
      const { data } = await axios.get(`${API}/admin/tax-report`, { params: { year } });
      setTaxReport(data);
    } catch (e) {
      toast.error("Failed to load tax report");
    }
  }, []);

  // Quarterly platform income-tax estimate
  const [quarterlyEstimate, setQuarterlyEstimate] = useState(null);
  const [estQuarter, setEstQuarter] = useState(((new Date().getMonth()) / 3 | 0) + 1);
  const loadQuarterlyEstimate = useCallback(async (year, quarter) => {
    try {
      const { data } = await axios.get(`${API}/admin/quarterly-tax-estimate`, {
        params: { year, quarter },
      });
      setQuarterlyEstimate(data);
    } catch (e) {
      toast.error("Failed to load quarterly estimate");
    }
  }, []);

  // Heatmap
  const [heatmap, setHeatmap] = useState(null);
  const [heatmapDays, setHeatmapDays] = useState(30);
  const loadHeatmap = useCallback(async (days) => {
    try {
      const { data } = await axios.get(`${API}/admin/heatmap`, { params: { days } });
      setHeatmap(data);
    } catch (e) {
      toast.error("Failed to load heatmap");
    }
  }, []);

  // Promo codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", discount_pct: "", discount_cents: "", max_uses: "", expires_at: "", description: "", active: true });
  const [promoSaving, setPromoSaving] = useState(false);
  const loadPromoCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setPromoCodes(data || []);
    } catch (e) {
      toast.error("Failed to load promo codes");
    }
  }, []);
  const savePromoCode = async () => {
    const code = promoForm.code.trim().toUpperCase();
    if (!code) { toast.error("Code is required"); return; }
    if (!promoForm.discount_pct && !promoForm.discount_cents) {
      toast.error("Set either a discount % or a flat $ off"); return;
    }
    setPromoSaving(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code,
        discount_pct: promoForm.discount_pct ? parseFloat(promoForm.discount_pct) : null,
        discount_cents: promoForm.discount_cents ? Math.round(parseFloat(promoForm.discount_cents) * 100) : null,
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses, 10) : null,
        expires_at: promoForm.expires_at || null,
        description: promoForm.description || "",
        active: !!promoForm.active,
      });
      toast.success(`${code} saved`);
      setPromoForm({ code: "", discount_pct: "", discount_cents: "", max_uses: "", expires_at: "", description: "", active: true });
      loadPromoCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setPromoSaving(false);
    }
  };
  const deletePromoCode = async (code) => {
    if (!window.confirm(`Delete promo code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${encodeURIComponent(code)}`);
      toast.success("Deleted");
      loadPromoCodes();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // Webhook events
  const [webhookEvents, setWebhookEvents] = useState([]);
  const loadWebhookEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/webhook-events`, { params: { limit: 100 } });
      setWebhookEvents(data.events || []);
    } catch (e) {
      toast.error("Failed to load webhook events");
    }
  }, []);

  useEffect(() => {
    if (activeTab === "taxes" && isAdmin) {
      loadTaxReport(taxYear);
      loadQuarterlyEstimate(taxYear, estQuarter);
    }
    if (activeTab === "heatmap" && isAdmin) loadHeatmap(heatmapDays);
    if (activeTab === "promo" && isAdmin) loadPromoCodes();
    if (activeTab === "webhooks" && isAdmin) loadWebhookEvents();
  }, [activeTab, taxYear, estQuarter, heatmapDays, isAdmin, loadTaxReport, loadQuarterlyEstimate, loadHeatmap, loadPromoCodes, loadWebhookEvents]);

  const downloadTaxCSV = () => {
    if (!taxReport) return;
    const rows = [
      ["Month", "Trips", "Gross (incl tax)", "Subtotal", "Sales Tax (PA 6%)", "Driver Payouts", "Platform Cut (20%)", "Tips"],
      ...Object.entries(taxReport.monthly).map(([m, b]) => [
        new Date(taxReport.year, parseInt(m, 10) - 1, 1).toLocaleString('en-US', { month: 'long' }),
        b.count, b.gross, b.subtotal, b.sales_tax, b.driver, b.platform, b.tips,
      ]),
      ["YEAR", taxReport.yearly.count, taxReport.yearly.gross, taxReport.yearly.subtotal,
        taxReport.yearly.sales_tax, taxReport.yearly.driver, taxReport.yearly.platform, taxReport.yearly.tips],
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `link-tax-report-${taxReport.year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Driver handlers
  const refreshDrivers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/drivers`);
      setDrivers(res.data || []);
    } catch (e) {
      toast.error("Failed to refresh drivers");
    }
  }, []);

  const approveDriver = async (id) => {
    setDriverActionId(id);
    try {
      await axios.post(`${API}/admin/drivers/${id}/approve`);
      toast.success("Driver approved");
      refreshDrivers();
    } catch (e) {
      toast.error("Approve failed");
    } finally {
      setDriverActionId(null);
    }
  };

  const openRejectDialog = (d) => {
    const name = [d.first_name, d.last_name].filter(Boolean).join(" ") || d.email || "this driver";
    setRejectTarget({ id: d.id, name });
    setRejectReason("");
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (!reason || reason.length < 5) {
      toast.error("Please enter a rejection reason (at least 5 characters)");
      return;
    }
    setRejectSubmitting(true);
    setDriverActionId(rejectTarget.id);
    try {
      await axios.post(`${API}/admin/drivers/${rejectTarget.id}/reject`, { reason });
      toast.success("Driver rejected");
      setRejectTarget(null);
      setRejectReason("");
      refreshDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setRejectSubmitting(false);
      setDriverActionId(null);
    }
  };

  const suspendDriver = async (id) => {
    setDriverActionId(id);
    try {
      await axios.post(`${API}/admin/drivers/${id}/suspend`);
      toast.success("Driver suspended");
      refreshDrivers();
    } catch (e) {
      toast.error("Suspend failed");
    } finally {
      setDriverActionId(null);
    }
  };

  const updateBackgroundCheck = async (id, status) => {
    setDriverActionId(id);
    try {
      await axios.post(`${API}/admin/drivers/${id}/background-check`, { status });
      toast.success(`Background check marked ${status}`);
      refreshDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setDriverActionId(null);
    }
  };

  const deleteDriver = async (id, name) => {
    if (!window.confirm(`Permanently delete ${name || "this driver"}? This wipes their account, documents, and push subs. Cannot be undone.`)) return;
    setDriverActionId(id);
    try {
      await axios.delete(`${API}/admin/drivers/${id}`);
      toast.success("Driver deleted");
      refreshDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDriverActionId(null);
    }
  };

  const bulkDeleteDrivers = async (params, label) => {
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(`${API}/admin/drivers`, { params });
      toast.success(`Deleted ${data.deleted} driver${data.deleted === 1 ? "" : "s"}`);
      refreshDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setSaving(false);
    }
  };

  // Maintenance handlers
  const wipeBookings = async (status) => {
    const label = { pending: "all PENDING", cancelled: "all CANCELLED", no_show: "all NO-SHOW" }[status] || status;
    if (!window.confirm(`Permanently delete ${label} bookings? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(`${API}/admin/bookings`, { params: { status } });
      toast.success(`Deleted ${data.deleted} bookings`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const wipeTestBookings = async () => {
    if (!window.confirm("Delete bookings that look like test data (Test/Smoke/Live customer names, 555-555 phones)?")) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(`${API}/admin/bookings`, { params: { test_only: true } });
      toast.success(`Deleted ${data.deleted} test bookings`);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const wipeAllExceptCustomer = async () => {
    const keep = window.prompt(
      "DELETE EVERY BOOKING EXCEPT the customer whose name you type below.\n\n" +
      "Type the customer name exactly (case-insensitive). Leave blank to cancel."
    );
    if (!keep || !keep.trim()) return;
    if (!window.confirm(`This will delete ALL bookings except those for "${keep.trim()}". Are you sure?`)) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(`${API}/admin/bookings`, {
        params: { keep_customer_name: keep.trim() }
      });
      toast.success(`Deleted ${data.deleted} bookings — only "${keep.trim()}" preserved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const wipeLegacyJobs = async () => {
    if (!window.confirm("Delete all legacy demo seed jobs (the multi-stop fake delivery jobs)? This won't affect real customer bookings.")) return;
    setSaving(true);
    try {
      const { data } = await axios.delete(`${API}/admin/jobs`);
      toast.success(`Deleted ${data.deleted} legacy demo jobs`);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const filteredDrivers = drivers.filter(d => {
    if (driverFilter === "all") return true;
    return (d.driver_status || "pending") === driverFilter;
  });

  // Plan handlers
  const updatePlan = (id, field, value) => {
    setPlans(prev => prev.map(p =>
      p.id === id
        ? { ...p, [field]: field === "name" || field === "description" ? value : parseFloat(value) || 0 }
        : p
    ));
  };

  const savePlan = async (id) => {
    setSaving(true);
    try {
      const p = plans.find(x => x.id === id);
      await axios.put(`${API}/admin/plans/${id}`, {
        name: p.name,
        price_cents: Math.round(p.price_cents),
        discount_pct: p.discount_pct,
        description: p.description,
      });
      toast.success(`${p.name} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-500">Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/driver")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">Admin Panel</h1>
            <p className="text-sm text-zinc-500">Manage pricing, specials & settings</p>
          </div>
        </div>
      </header>

      <main className="px-5 py-6">
        {/* Trial countdown pill — auto-hides for the flagship tenant, so
            the platform owner never sees a self-referential banner. */}
        <div className="mb-4">
          <TenantTrialBanner />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile: horizontally scrollable single row (10 tabs would stack 3 deep
              on a 390px screen and overlap content). md+ uses a 10-col grid. */}
          <TabsList className="w-full bg-[#121212] border border-white/5 p-1 mb-6 flex md:grid md:grid-cols-10 gap-1 overflow-x-auto overflow-y-hidden md:overflow-visible no-scrollbar [-webkit-overflow-scrolling:touch]">
            <TabsTrigger value="pricing" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-pricing">
              <DollarSign className="w-4 h-4 mr-1" />Pricing
            </TabsTrigger>
            <TabsTrigger value="specials" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-specials">
              <Tag className="w-4 h-4 mr-1" />Specials
            </TabsTrigger>
            <TabsTrigger value="promo" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-promo">
              <Tag className="w-4 h-4 mr-1" />Promo
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-plans">
              <Crown className="w-4 h-4 mr-1" />Plans
            </TabsTrigger>
            <TabsTrigger value="drivers" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-drivers">
              <Users className="w-4 h-4 mr-1" />Drivers
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-customers">
              <Users className="w-4 h-4 mr-1" />Customers
            </TabsTrigger>
            <TabsTrigger value="taxes" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-taxes">
              <FileImage className="w-4 h-4 mr-1" />Taxes
            </TabsTrigger>
            <TabsTrigger value="heatmap" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-heatmap">
              <MapPin className="w-4 h-4 mr-1" />Heatmap
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-webhooks">
              <Radio className="w-4 h-4 mr-1" />Webhooks
            </TabsTrigger>
            <TabsTrigger value="franchise" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-franchise">
              <Building2 className="w-4 h-4 mr-1" />Franchise
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex-shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-white" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-1" />Settings
            </TabsTrigger>
          </TabsList>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-4">
            <p className="text-sm text-zinc-400 mb-4">Set base price and per-mile rate for each service</p>
            
            {services.map(service => (
              <Card key={service.id} className="bg-[#121212] border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#4D88FF]" />
                    {service.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-zinc-400 text-sm">Description</Label>
                    <Input
                      value={service.description}
                      onChange={(e) => updateService(service.id, 'description', e.target.value)}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-zinc-400 text-sm">Base ($)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={service.basePrice}
                        onChange={(e) => updateService(service.id, 'basePrice', e.target.value)}
                        className="bg-zinc-800 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Per Mile ($)</Label>
                      <Input
                        type="number"
                        step="0.05"
                        value={service.perMile}
                        onChange={(e) => updateService(service.id, 'perMile', e.target.value)}
                        className="bg-zinc-800 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Min ($)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={service.minPrice ?? 0}
                        onChange={(e) => updateService(service.id, 'minPrice', e.target.value)}
                        className="bg-zinc-800 border-white/10 text-white mt-1"
                        data-testid={`min-${service.id}`}
                      />
                    </div>
                  </div>
                  <div className="bg-zinc-800 rounded p-3">
                    <p className="text-xs text-zinc-500">
                      30 mi = <span className="text-emerald-400 font-semibold">${Math.max((service.minPrice ?? 0), service.basePrice + service.perMile * 30).toFixed(2)}</span>
                      {" · "}
                      Min fare: <span className="text-[#4D88FF] font-semibold">${(service.minPrice ?? 0).toFixed(2)}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button onClick={saveServices} disabled={saving} className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0">
              <Save className="w-5 h-5 mr-2" />{saving ? "Saving..." : "Save Pricing"}
            </Button>
          </TabsContent>

          {/* Specials Tab */}
          <TabsContent value="specials" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-400">City discounts for specific days</p>
              <Button onClick={addSpecial} size="sm" variant="outline" className="border-[#0047FF]/30 text-[#4D88FF] hover:bg-[#0047FF]/10">
                <Plus className="w-4 h-4 mr-2" />Add Special
              </Button>
            </div>
            
            {specials.map(special => (
              <Card key={special.id} className="bg-[#121212] border-white/5">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="grid grid-cols-2 gap-3 flex-1">
                      <div>
                        <Label className="text-zinc-400 text-sm">City</Label>
                        <Input
                          value={special.city}
                          onChange={(e) => updateSpecial(special.id, 'city', e.target.value)}
                          className="bg-zinc-800 border-white/10 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Discount %</Label>
                        <Input
                          type="number"
                          min="1"
                          max="50"
                          value={special.discount}
                          onChange={(e) => updateSpecial(special.id, 'discount', e.target.value)}
                          className="bg-zinc-800 border-white/10 text-white mt-1"
                        />
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeSpecial(special.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-2">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400 text-sm mb-2 block">Active Days</Label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.id}
                          onClick={() => toggleSpecialDay(special.id, day.id)}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            special.days.includes(day.id)
                              ? 'bg-[#0047FF] text-black'
                              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-zinc-800 rounded p-3">
                    <p className="text-xs text-zinc-500">
                      Preview: <span className="text-[#4D88FF]">{special.city} {special.discount}% off on {special.days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}

            {specials.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No specials configured</p>
              </div>
            )}

            <Button onClick={saveSpecials} disabled={saving} className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0">
              <Save className="w-5 h-5 mr-2" />{saving ? "Saving..." : "Save Specials"}
            </Button>
          </TabsContent>

          {/* Plans Tab (Link Pro subscriptions) */}
          <TabsContent value="plans" className="space-y-4" data-testid="plans-tab-content">
            <p className="text-sm text-zinc-400">
              Set the price and discount for each Link Pro subscription plan. Customers see these on their Account page.
            </p>

            {plans.filter(p => p.active !== false).map(plan => (
              <Card key={plan.id} className="bg-[#121212] border-white/5" data-testid={`plan-edit-${plan.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[#FFD369]" />
                    {plan.name}
                    <Badge className="bg-[#0047FF]/10 text-[#4D88FF] border-[#0047FF]/20 text-[10px] ml-auto">
                      per {plan.interval}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-zinc-400 text-sm">Display Name</Label>
                    <Input
                      value={plan.name}
                      onChange={(e) => updatePlan(plan.id, 'name', e.target.value)}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Description</Label>
                    <Input
                      value={plan.description || ''}
                      onChange={(e) => updatePlan(plan.id, 'description', e.target.value)}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400 text-sm">Price (¢)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={plan.price_cents}
                        onChange={(e) => updatePlan(plan.id, 'price_cents', e.target.value)}
                        className="bg-zinc-800 border-white/10 text-white mt-1"
                        data-testid={`plan-price-${plan.id}`}
                      />
                      <p className="text-[10px] text-zinc-500 mt-1">Currently: ${(plan.price_cents/100).toFixed(2)} / {plan.interval}</p>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Discount %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={plan.discount_pct}
                        onChange={(e) => updatePlan(plan.id, 'discount_pct', e.target.value)}
                        className="bg-zinc-800 border-white/10 text-white mt-1"
                        data-testid={`plan-discount-${plan.id}`}
                      />
                      <p className="text-[10px] text-zinc-500 mt-1">Applied to every booking</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => savePlan(plan.id)}
                    disabled={saving}
                    className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl border-0"
                    data-testid={`save-plan-${plan.id}`}
                  >
                    <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : `Save ${plan.name}`}
                  </Button>
                </CardContent>
              </Card>
            ))}

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
              <p className="text-xs text-amber-300">
                <strong>Stripe webhook needed:</strong> point a Stripe webhook at <code className="text-amber-100 bg-black/30 px-1 rounded">/api/webhook/stripe-subscriptions</code> for events:
                {" "}<code>checkout.session.completed</code>, <code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>, <code>invoice.payment_failed</code>. Then set <code className="text-amber-100 bg-black/30 px-1 rounded">STRIPE_SUBSCRIPTION_WEBHOOK_SECRET</code> in backend/.env and redeploy.
              </p>
            </div>
          </TabsContent>

          {/* Drivers Tab */}
          <TabsContent value="drivers" className="space-y-4" data-testid="drivers-tab-content">
            <AdminUserFix />
            <CustomerLookupCard />
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                Review applications, approve drivers and manage active roster
              </p>
              <Button onClick={refreshDrivers} variant="outline" size="sm" className="border-white/10 text-zinc-300 hover:bg-zinc-800">
                Refresh
              </Button>
            </div>

            {/* Status filter pills */}
            <div className="flex gap-2 overflow-x-auto">
              {[
                { id: "pending",   label: "Pending",   count: drivers.filter(d => (d.driver_status || "pending") === "pending").length },
                { id: "approved",  label: "Approved",  count: drivers.filter(d => d.driver_status === "approved").length },
                { id: "suspended", label: "Suspended", count: drivers.filter(d => d.driver_status === "suspended").length },
                { id: "rejected",  label: "Rejected",  count: drivers.filter(d => d.driver_status === "rejected").length },
                { id: "all",       label: "All",       count: drivers.length },
              ].map(pill => (
                <button
                  key={pill.id}
                  onClick={() => setDriverFilter(pill.id)}
                  data-testid={`driver-filter-${pill.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    driverFilter === pill.id
                      ? "bg-[#0047FF] text-white"
                      : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                  }`}
                >
                  {pill.label} <span className="opacity-70">({pill.count})</span>
                </button>
              ))}
            </div>

            {/* Cleanup tools — wipe fake/test/rejected applicants */}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                onClick={() => bulkDeleteDrivers({ test_only: true }, "all TEST/FAKE driver applicants")}
                variant="outline"
                size="sm"
                disabled={saving}
                data-testid="cleanup-test-drivers"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete test/fake applicants
              </Button>
              <Button
                onClick={() => bulkDeleteDrivers({ status: "rejected" }, "all REJECTED drivers")}
                variant="outline"
                size="sm"
                disabled={saving}
                data-testid="cleanup-rejected-drivers"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete all rejected
              </Button>
              <Button
                onClick={() => bulkDeleteDrivers({ status: "pending" }, "all PENDING (un-reviewed) drivers")}
                variant="outline"
                size="sm"
                disabled={saving}
                data-testid="cleanup-pending-drivers"
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete all pending
              </Button>
            </div>

            {filteredDrivers.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No drivers in this list</p>
              </div>
            ) : (
              filteredDrivers.map(d => {
                const status = d.driver_status || "pending";
                const docs = d.documents || {};
                return (
                  <Card key={d.id} className="bg-[#121212] border-white/5" data-testid={`driver-row-${d.id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-white truncate">
                              {d.first_name} {d.last_name}
                            </h3>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                              status === "approved"
                                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                                : status === "suspended"
                                  ? "bg-red-500/10 text-red-400 border-red-500/20"
                                  : status === "rejected"
                                    ? "bg-rose-600/10 text-rose-400 border-rose-600/30"
                                    : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                            }`}>
                              {status}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Applied {new Date(d.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-zinc-400">
                        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-zinc-500" />{d.email}</div>
                        <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-zinc-500" />{d.phone}</div>
                        {(d.address || d.city) && (
                          <div className="flex items-center gap-2 col-span-full"><MapPin className="w-3.5 h-3.5 text-zinc-500" />
                            {[d.address, d.city, d.state, d.zip_code].filter(Boolean).join(", ")}
                          </div>
                        )}
                        {(d.vehicle_make || d.vehicle_model) && (
                          <div className="flex items-center gap-2 col-span-full"><Car className="w-3.5 h-3.5 text-zinc-500" />
                            {[d.vehicle_year, d.vehicle_color, d.vehicle_make, d.vehicle_model].filter(Boolean).join(" ")}
                            {d.license_plate ? ` · ${d.license_plate}` : ""}
                          </div>
                        )}
                      </div>

                      {status === "rejected" && d.rejection_reason && (
                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/5 border border-red-500/20" data-testid={`rejection-reason-${d.id}`}>
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <div className="text-xs">
                            <p className="text-red-300 font-medium uppercase tracking-wide text-[10px] mb-0.5">Rejection reason</p>
                            <p className="text-zinc-300 leading-snug">{d.rejection_reason}</p>
                          </div>
                        </div>
                      )}

                      {/* Background check */}
                      <div className="flex items-center gap-2 flex-wrap pt-1">
                        {(() => {
                          const bg = d.background_check_status || "not_started";
                          const palette = {
                            not_started: "bg-zinc-700/30 text-zinc-400 border-zinc-600/30",
                            pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
                            clear: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
                            flagged: "bg-red-500/10 text-red-400 border-red-500/30",
                          }[bg];
                          return (
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${palette}`}>
                              BG check: {bg.replace("_", " ")}
                            </span>
                          );
                        })()}
                        <select
                          value={d.background_check_status || "not_started"}
                          onChange={(e) => updateBackgroundCheck(d.id, e.target.value)}
                          disabled={driverActionId === d.id}
                          data-testid={`bg-select-${d.id}`}
                          className="text-[11px] bg-zinc-800 border border-white/10 text-white rounded px-2 py-1"
                        >
                          <option value="not_started">Not started</option>
                          <option value="pending">Pending</option>
                          <option value="clear">Clear</option>
                          <option value="flagged">Flagged</option>
                        </select>
                        {d.vehicle_year && (
                          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                            parseInt(d.vehicle_year, 10) >= 2016
                              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                              : "bg-red-500/10 text-red-400 border-red-500/30"
                          }`}>
                            Vehicle {d.vehicle_year} {parseInt(d.vehicle_year, 10) >= 2016 ? "✓" : "✗"}
                          </span>
                        )}
                      </div>

                      {/* Documents */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {["license_photo", "insurance_photo"].map(key => {
                          const doc = docs?.[key];
                          const label = key === "license_photo" ? "License" : "Insurance";
                          if (!doc?.storage_path && !doc?.filename) {
                            return (
                              <span key={key} className="text-[11px] px-2 py-1 rounded bg-zinc-800 text-zinc-500 border border-white/5">
                                <FileImage className="w-3 h-3 inline mr-1" />{label}: missing
                              </span>
                            );
                          }
                          // Prefer object storage (path-aware route); fall back to
                          // the legacy /uploads static route for older records.
                          const token = localStorage.getItem('linklite_token') || '';
                          const href = doc.storage_path
                            ? `${API}/files/${doc.storage_path}?auth=${encodeURIComponent(token)}`
                            : `${API}/uploads/${doc.filename}`;
                          return (
                            <a
                              key={key}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`driver-doc-${key}-${d.id}`}
                              className="text-[11px] px-2 py-1 rounded bg-[#0047FF]/15 text-[#4D88FF] border border-[#0047FF]/30 hover:bg-[#0047FF]/25"
                            >
                              <FileImage className="w-3 h-3 inline mr-1" />View {label}
                            </a>
                          );
                        })}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {status !== "approved" && (
                          <Button
                            size="sm"
                            disabled={driverActionId === d.id}
                            onClick={() => approveDriver(d.id)}
                            data-testid={`approve-${d.id}`}
                            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />Approve
                          </Button>
                        )}
                        {status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={driverActionId === d.id}
                            onClick={() => openRejectDialog(d)}
                            data-testid={`reject-${d.id}`}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4 mr-1" />Reject
                          </Button>
                        )}
                        {status === "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={driverActionId === d.id}
                            onClick={() => suspendDriver(d.id)}
                            data-testid={`suspend-${d.id}`}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            Suspend
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={driverActionId === d.id}
                          onClick={async () => {
                            if (!window.confirm(
                              `Attribute every completed trip to ${d.first_name || d.email}?\n\n` +
                              `Grabs BOTH trips that have no driver AND trips that were placeholder-claimed by an admin account (so the "Link Admin · 2 trips $33.98" row in your tax breakdown rolls back into the real driver's earnings + 1099).\n\n` +
                              `Safe — won't touch trips already attributed to another working driver.`
                            )) return;
                            setDriverActionId(d.id);
                            try {
                              const { data } = await axios.post(`${API}/admin/bookings/claim-unassigned`, { driver_id: d.id });
                              if (data.updated === 0) {
                                toast.info("No unassigned completed trips found — everything is already attributed.", { duration: 8000 });
                              } else {
                                toast.success(`Attributed ${data.updated} trip${data.updated === 1 ? '' : 's'} to ${data.driver_name}. Your earnings + 1099 will now include them.`, { duration: 10000 });
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Claim failed");
                            } finally {
                              setDriverActionId(null);
                            }
                          }}
                          data-testid={`claim-unassigned-${d.id}`}
                          className="border-blue-500/40 text-blue-200 hover:bg-blue-500/10"
                          title="Attribute every completed trip without a driver to this person"
                        >
                          <Truck className="w-4 h-4 mr-1" />Claim trips
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={driverActionId === d.id}
                          onClick={async () => {
                            const newPw = window.prompt(
                              `Set a new password for ${d.first_name || d.email}.\n\nMin 8 characters. They'll be able to sign in with this password immediately, and any active lockout will be cleared.`,
                              ""
                            );
                            if (!newPw) return;
                            if (newPw.length < 8) {
                              toast.error("Password must be at least 8 characters");
                              return;
                            }
                            try {
                              await axios.post(`${API}/admin/users/${d.id}/reset-password`, { new_password: newPw });
                              toast.success(`Password reset for ${d.first_name || d.email}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Reset failed");
                            }
                          }}
                          data-testid={`reset-password-${d.id}`}
                          className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                          title="Reset this driver's password"
                        >
                          <KeyRound className="w-4 h-4 mr-1" />Password
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={driverActionId === d.id}
                          onClick={() => deleteDriver(d.id, `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.email)}
                          data-testid={`delete-driver-${d.id}`}
                          className="border-red-700/40 text-red-300 hover:bg-red-700/10"
                          title="Permanently delete this driver"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Customers Tab — directory + lookup */}
          <TabsContent value="customers" className="space-y-4" data-testid="customers-tab-content">
            <CustomerLookupCard initialEmail={lookupInitialEmail} bumpKey={lookupBump} />

            {/* Test-customer cleanup — safe preview-then-commit flow */}
            <Card className="bg-zinc-900 border-amber-500/30" data-testid="wipe-test-customers-card">
              <CardHeader>
                <CardTitle className="text-amber-300 text-base flex items-center gap-2">
                  Bulk clean test customers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Preview shows which customers MATCH test heuristics (emails containing
                  <span className="text-amber-300 font-mono mx-1">test/example/qa/dummy/+</span>,
                  names starting with <span className="text-amber-300 font-mono mx-1">Test/Flow</span>,
                  dummy phones) AND have zero wallet credits, zero completed bookings, and no
                  active subscription. Real customers are always skipped.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={previewWipeTestCustomers}
                    disabled={wipeLoading}
                    variant="outline"
                    className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                    data-testid="wipe-preview-button"
                  >
                    {wipeLoading ? "Scanning…" : "Preview test customers"}
                  </Button>
                  {wipePreview && wipePreview.would_delete > 0 && (
                    <Button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Permanently delete ${wipePreview.would_delete} test customer accounts?\n\nReal users (with credits / completed bookings / subscriptions) are excluded.\n\nThis cannot be undone.`)) {
                          commitWipeTestCustomers();
                        }
                      }}
                      disabled={wipeCommitting}
                      className="bg-red-500 hover:bg-red-400 text-white font-bold"
                      data-testid="wipe-commit-button"
                    >
                      {wipeCommitting ? "Deleting…" : `Delete ${wipePreview.would_delete} test customers`}
                    </Button>
                  )}
                  {wipePreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setWipePreview(null)}
                      className="text-zinc-400 hover:text-white"
                      data-testid="wipe-clear-button"
                    >
                      Clear
                    </Button>
                  )}
                </div>

                {wipePreview && (
                  <div className="mt-2 space-y-2 text-xs" data-testid="wipe-preview-result">
                    <div className="flex flex-wrap gap-3 text-zinc-300">
                      <span><strong className="text-white">{wipePreview.matched}</strong> matched</span>
                      <span className="text-red-400"><strong>{wipePreview.would_delete}</strong> will be deleted</span>
                      <span className="text-emerald-400"><strong>{wipePreview.skipped_real_users}</strong> real users skipped</span>
                    </div>
                    {wipePreview.preview?.length > 0 && (
                      <details open className="bg-black/30 rounded p-2 border border-white/5">
                        <summary className="cursor-pointer text-zinc-300 font-semibold">To be deleted ({wipePreview.would_delete}) — preview</summary>
                        <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                          {wipePreview.preview.map((c) => (
                            <li key={c.id} className="font-mono text-[11px] text-zinc-400 truncate" data-testid={`wipe-preview-row-${c.id}`}>
                              {c.email || '(no email)'} · {c.first_name || ''} {c.last_name || ''} · {c.phone || '—'}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {wipePreview.skipped?.length > 0 && (
                      <details className="bg-black/30 rounded p-2 border border-white/5">
                        <summary className="cursor-pointer text-zinc-300 font-semibold">Skipped real users ({wipePreview.skipped_real_users})</summary>
                        <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                          {wipePreview.skipped.map((c) => (
                            <li key={c.id} className="font-mono text-[11px] text-emerald-300/70 truncate">
                              {c.email || '(no email)'} — {(c.skip_reasons || []).join(' · ')}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-base">All customers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <form
                  onSubmit={(e) => { e.preventDefault(); fetchCustomers(customerSearch); }}
                  className="flex gap-2"
                  data-testid="customers-search-form"
                >
                  <Input
                    placeholder="Search by name, email, or phone…"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="bg-zinc-800 border-white/10 text-white"
                    data-testid="customers-search-input"
                  />
                  <Button type="submit" variant="outline" className="border-white/15 text-zinc-200 hover:bg-zinc-800" data-testid="customers-search-button">
                    Search
                  </Button>
                  {customerSearch && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-zinc-400 hover:text-white"
                      onClick={() => { setCustomerSearch(""); fetchCustomers(""); }}
                      data-testid="customers-search-clear"
                    >
                      Clear
                    </Button>
                  )}
                </form>

                {customersLoading ? (
                  <div className="text-center py-8 text-zinc-500" data-testid="customers-loading">Loading customers…</div>
                ) : customers.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500" data-testid="customers-empty">No customers yet.</div>
                ) : (
                  <div className="space-y-2" data-testid="customers-list">
                    <p className="text-xs uppercase tracking-wider text-zinc-500">
                      {customers.length} customer{customers.length === 1 ? '' : 's'}
                    </p>
                    {customers.map((c) => {
                      const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
                      const handleSelect = () => {
                        if (!c.email) {
                          toast.error("This customer has no email on file");
                          return;
                        }
                        setLookupInitialEmail(c.email);
                        setLookupBump((n) => n + 1);
                      };
                      const handleDelete = (e) => {
                        e.stopPropagation();
                        if (!c.id) {
                          toast.error("Missing user id");
                          return;
                        }
                        // Open the audit dialog instead of stacking window.confirms — the
                        // dialog pulls a JIM / Cash breakdown so admin can square the
                        // books before the wipe.
                        setDeleteTarget(c);
                      };
                      return (
                        <div
                          key={c.id || c.email || c.phone}
                          className="w-full rounded-lg bg-zinc-800/50 border border-white/5 p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 hover:bg-zinc-800 hover:border-[#0047FF]/40 transition-colors"
                          data-testid={`customer-row-${c.id || c.email}`}
                        >
                          <button
                            type="button"
                            onClick={handleSelect}
                            className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer"
                            data-testid={`customer-row-select-${c.id || c.email}`}
                          >
                            <p className="text-sm font-semibold text-white truncate">{name}</p>
                            <p className="text-xs text-zinc-400 truncate">
                              {c.email || "—"} · {c.phone || "—"}
                            </p>
                            {c.last_booking_at && (
                              <p className="text-[10px] text-zinc-500 mt-0.5">
                                Last booking: {new Date(c.last_booking_at).toLocaleDateString()}
                              </p>
                            )}
                          </button>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <Badge variant="outline" className="border-white/15 text-zinc-200">
                              {c.total_bookings || 0} bookings
                            </Badge>
                            {c.completed_bookings > 0 && (
                              <Badge variant="outline" className="border-emerald-500/30 text-emerald-300">
                                {c.completed_bookings} done
                              </Badge>
                            )}
                            {c.lifetime_spend > 0 && (
                              <Badge variant="outline" className="border-amber-500/30 text-amber-300">
                                ${c.lifetime_spend.toFixed(2)}
                              </Badge>
                            )}
                            {c.wallet_credits_cents > 0 && (
                              <Badge variant="outline" className="border-violet-500/30 text-violet-300">
                                ${((c.wallet_credits_cents || 0) / 100).toFixed(2)} credits
                              </Badge>
                            )}
                            {c.subscription_status === "active" && (
                              <Badge variant="outline" className="border-yellow-500/30 text-yellow-300">
                                Link Pro
                              </Badge>
                            )}
                            <button
                              type="button"
                              onClick={handleDelete}
                              className="ml-1 text-[11px] font-semibold text-rose-300 hover:text-white hover:bg-rose-500/80 border border-rose-500/30 px-2 py-1 rounded transition-colors"
                              data-testid={`customer-delete-${c.id || c.email}`}
                              title="Delete this customer account"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Taxes & Reporting Tab */}
          <TabsContent value="taxes" className="space-y-4" data-testid="taxes-tab-content">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">Year-end tax + revenue summary. All amounts in USD.</p>
              <div className="flex items-center gap-2">
                <select
                  value={taxYear}
                  onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                  className="text-xs bg-zinc-800 border border-white/10 text-white rounded px-2 py-1.5"
                  data-testid="tax-year-select"
                >
                  {[0, 1, 2].map(o => {
                    const y = new Date().getFullYear() - o;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
                <Button onClick={downloadTaxCSV} disabled={!taxReport} variant="outline" size="sm" className="border-white/10 text-zinc-300 hover:bg-zinc-800" data-testid="download-tax-csv">
                  Export CSV
                </Button>
                <Button
                  onClick={async () => {
                    if (!taxReport) return;
                    try {
                      const token = localStorage.getItem("link_token") || localStorage.getItem("token");
                      const res = await fetch(`${API}/admin/business-tax-package?year=${taxReport.year}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) throw new Error("Download failed");
                      const blob = await res.blob();
                      const a = document.createElement("a");
                      const u = URL.createObjectURL(blob);
                      a.href = u;
                      a.download = `link_business_tax_package_${taxReport.year}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(u);
                      toast.success("Tax package downloaded — hand it to your CPA");
                    } catch (e) {
                      toast.error("Failed to download tax package");
                    }
                  }}
                  disabled={!taxReport}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
                  data-testid="download-business-tax-pdf"
                  title="Schedule C + SE + PA Sales Tax + Mercantile + 1040-ES + 1099 roster — one PDF for your CPA"
                >
                  CPA Package PDF
                </Button>
                <Button
                  onClick={() => taxReport && window.open(`/admin/taxes/print?year=${taxReport.year}`, '_blank')}
                  disabled={!taxReport}
                  size="sm"
                  className="bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold"
                  data-testid="print-tax-summary-btn"
                >
                  Print Tax Summary
                </Button>
              </div>
            </div>

            {!taxReport ? (
              <div className="text-center py-8 text-zinc-500">Loading…</div>
            ) : (
              <>
                {/* Quarterly platform income-tax estimate */}
                <Card className="bg-[#121212] border-amber-500/20" data-testid="quarterly-estimate-card">
                  <CardHeader>
                    <CardTitle className="text-base font-medium text-white flex items-center justify-between flex-wrap gap-2">
                      <span>Quarterly platform income-tax estimate</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map(q => (
                          <button
                            key={q}
                            onClick={() => setEstQuarter(q)}
                            data-testid={`quarter-${q}`}
                            className={`px-2.5 py-1 rounded text-xs font-bold ${
                              estQuarter === q ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                            }`}
                          >
                            Q{q}
                          </button>
                        ))}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!quarterlyEstimate ? (
                      <p className="text-sm text-zinc-500">Loading…</p>
                    ) : (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div>
                            <p className="text-zinc-500 uppercase tracking-wider font-bold">Period</p>
                            <p className="text-white mt-1">{quarterlyEstimate.period.start} → {quarterlyEstimate.period.end}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500 uppercase tracking-wider font-bold">Trips</p>
                            <p className="text-white mt-1">{quarterlyEstimate.completed_trips}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500 uppercase tracking-wider font-bold">Platform Revenue</p>
                            <p className="text-emerald-400 mt-1">${quarterlyEstimate.platform_revenue.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500 uppercase tracking-wider font-bold">Net Taxable</p>
                            <p className="text-white mt-1">${quarterlyEstimate.net_taxable_income.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="rounded-lg bg-zinc-900/50 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div>
                            <p className="text-zinc-500">Federal ({quarterlyEstimate.rates.federal_pct}%)</p>
                            <p className="text-white font-bold mt-0.5">${quarterlyEstimate.estimated_taxes.federal.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">PA State ({quarterlyEstimate.rates.state_pct}%)</p>
                            <p className="text-white font-bold mt-0.5">${quarterlyEstimate.estimated_taxes.state_pa.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Self-Empl. ({quarterlyEstimate.rates.self_employment_pct}%)</p>
                            <p className="text-white font-bold mt-0.5">${quarterlyEstimate.estimated_taxes.self_employment.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-amber-400 uppercase tracking-wider font-bold">Total Q{quarterlyEstimate.quarter} Due</p>
                            <p className="text-amber-300 font-black text-base mt-0.5">${quarterlyEstimate.estimated_taxes.total_due.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          📅 Pay by <span className="text-amber-300 font-bold">{quarterlyEstimate.deadline}</span> · {quarterlyEstimate.form_reference}
                          <br />⚠️ {quarterlyEstimate.disclaimer}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* YTD summary tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Gross Revenue", val: `$${taxReport.yearly.gross.toFixed(2)}`, tone: "text-white" },
                    { label: "Sales Tax Collected", val: `$${taxReport.yearly.sales_tax.toFixed(2)}`, tone: "text-amber-400" },
                    { label: "Platform Cut (20%)", val: `$${taxReport.yearly.platform.toFixed(2)}`, tone: "text-emerald-400" },
                    { label: "Driver Payouts", val: `$${taxReport.yearly.driver.toFixed(2)}`, tone: "text-[#4D88FF]" },
                  ].map(t => (
                    <Card key={t.label} className="bg-[#121212] border-white/5">
                      <CardContent className="p-4">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{t.label}</p>
                        <p className={`text-xl font-black mt-1 ${t.tone}`} style={{ fontFamily: "Outfit, system-ui" }}>{t.val}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Payment-method breakdown — shows JIM / cash / Stripe / wallet split */}
                {/* Untracked JIM + cash trips — admin force-complete card */}
                {taxReport.untracked_offline?.length > 0 && (
                  <Card className="bg-amber-500/5 border-amber-500/40" data-testid="untracked-offline-card">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-amber-300">
                            ⚠️ {taxReport.untracked_offline.length} offline trip{taxReport.untracked_offline.length === 1 ? '' : 's'} not marked completed
                          </p>
                          <p className="text-[11px] text-amber-200/80 leading-relaxed mt-1">
                            These trips never reached <strong>completed</strong> status, so they&apos;re invisible to your tax totals. The <span className="text-amber-300 font-semibold">JIM</span> / <span className="text-emerald-300 font-semibold">Cash</span> / <span className="text-blue-300 font-semibold">Stripe</span> badge shows how the customer paid. Click <strong>Mark completed</strong> to roll them into this year&apos;s revenue (stamps the current time as the completion date).
                          </p>
                          <p className="text-[10px] text-amber-200/60 mt-2">
                            A daily digest of trips stuck &gt; 24h is auto-emailed to <strong>link@linklitepro.com</strong>. You can send a copy now to verify deliverability.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 shrink-0 h-7 px-2 text-[11px]"
                          data-testid="send-stuck-digest-now"
                          onClick={async () => {
                            try {
                              const { data } = await axios.post(`${API}/admin/stuck-offline-digest/check`, null, { params: { force: true } });
                              if (data.ok) toast.success(`Digest emailed (${data.stuck_count} trip${data.stuck_count === 1 ? '' : 's'})`);
                              else toast.error(data.error || data.reason || "Send failed");
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed to send digest");
                            }
                          }}
                        >
                          Email digest now
                        </Button>
                      </div>
                      <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {taxReport.untracked_offline.map((b) => {
                          const isJim = b._method_label === 'JIM';
                          const isStripe = b._method_label === 'Stripe';
                          const badgeColor = isJim
                            ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
                            : isStripe
                            ? 'bg-blue-500/20 text-blue-200 border-blue-500/40'
                            : 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40';
                          return (
                            <div
                              key={b.id}
                              className="flex items-center justify-between gap-2 bg-black/30 rounded p-2 border border-white/5 text-xs"
                              data-testid={`untracked-offline-row-${b.id}`}
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-white truncate flex items-center gap-2">
                                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${badgeColor}`}>
                                    {b._method_label}
                                  </span>
                                  <span className="truncate">{b.customer_name || '—'} · ${(b.estimated_price || 0).toFixed(2)}</span>
                                  <span className="text-zinc-500 font-normal text-[10px]">{b.status}</span>
                                </p>
                                <p className="text-zinc-400 text-[10px] truncate">
                                  {b.scheduled_date || b.created_at?.slice(0, 10)} · {b.pickup_address?.slice(0, 24)}… → {b.dropoff_address?.slice(0, 24)}…
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 shrink-0 h-7 px-2 text-[11px]"
                                data-testid={`force-complete-${b.id}`}
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/admin/bookings/${b.id}/force-complete`);
                                    toast.success(`Marked ${b.customer_name || 'trip'} completed`);
                                    loadTaxReport(taxReport.year);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Could not close trip");
                                  }
                                }}
                              >
                                Mark completed
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-[#121212] border-white/5" data-testid="payment-method-breakdown-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Booking revenue by payment method</p>
                      <span className="text-[10px] text-zinc-500">YTD</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {[
                        { key: 'stripe',  label: 'Stripe',     color: 'text-[#80B0FF]' },
                        { key: 'jim',     label: 'JIM',        color: 'text-amber-300' },
                        { key: 'cash',    label: 'Cash',       color: 'text-emerald-300' },
                        { key: 'credits', label: 'Wallet',     color: 'text-purple-300' },
                        { key: 'other',   label: 'Other',      color: 'text-zinc-400' },
                      ].map((m) => {
                        const bucket = taxReport.by_payment_method?.[m.key] || { gross: 0, count: 0 };
                        return (
                          <div key={m.key} className="bg-black/30 rounded-lg p-3 border border-white/5" data-testid={`pm-${m.key}`}>
                            <p className={`text-[10px] uppercase tracking-wider font-bold ${m.color}`}>{m.label}</p>
                            <p className="text-lg font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                              ${bucket.gross.toFixed(2)}
                            </p>
                            <p className="text-[10px] text-zinc-500">{bucket.count} trip{bucket.count === 1 ? '' : 's'}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Subscription revenue + Wallet deposits + Marketing write-offs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="extra-revenue-cards">
                  <Card className="bg-[#121212] border-[#0047FF]/20" data-testid="subscriptions-card">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-wider text-[#80B0FF] font-bold">Link Pro subscriptions</p>
                        <span className="text-[10px] text-[#80B0FF]/60">{taxReport.subscriptions?.yearly?.count || 0} payment(s)</span>
                      </div>
                      <p className="text-2xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                        ${(taxReport.subscriptions?.yearly?.gross || 0).toFixed(2)}
                      </p>
                      <p className="text-[11px] text-zinc-500">
                        Recurring SaaS income · tracked separately from ride taxes.
                        Sales-tax applicability: <strong>ask your CPA</strong> (PA generally does not tax digital subscriptions).
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#121212] border-emerald-500/20" data-testid="deposits-card">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Wallet deposits</p>
                        <span className="text-[10px] text-emerald-300/60">{taxReport.deposits?.yearly?.count || 0} deposit(s)</span>
                      </div>
                      <p className="text-2xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                        ${(taxReport.deposits?.yearly?.gross || 0).toFixed(2)}
                      </p>
                      <p className="text-[11px] text-zinc-500">
                        Customer credit-pack purchases. Income at deposit time (cash basis); the booking sales tax is collected separately when they redeem.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#121212] border-red-500/20" data-testid="writeoffs-card">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-wider text-red-300 font-bold">Marketing write-offs</p>
                        <span className="text-[10px] text-red-300/60">{taxReport.writeoffs?.yearly?.count || 0} grant(s)</span>
                      </div>
                      <p className="text-2xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                        ${(taxReport.writeoffs?.yearly?.total || 0).toFixed(2)}
                      </p>
                      <div className="text-[11px] text-zinc-500 space-y-0.5">
                        <p>Signup bonuses: <span className="text-zinc-300">${(taxReport.writeoffs?.yearly?.signup_bonus || 0).toFixed(2)}</span></p>
                        <p>Link Pro comps: <span className="text-zinc-300">${(taxReport.writeoffs?.yearly?.admin_pro_comp || 0).toFixed(2)}</span></p>
                        <p>Manual credit grants: <span className="text-zinc-300">${(taxReport.writeoffs?.yearly?.admin_credit_grant || 0).toFixed(2)}</span></p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Stripe backfill — reconciles past subscriptions/deposits that
                    pre-date the revenue_events tracker (e.g. Mckayla's signup). */}
                <Card className="bg-[#121212] border-amber-500/20" data-testid="stripe-backfill-card">
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-amber-300">Reconcile from Stripe</p>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Pulls every paid Stripe invoice + credit-pack session for {taxReport.year} and adds any missing entries to the totals above. Idempotent — safe to re-run.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                      data-testid="stripe-backfill-btn"
                      onClick={async () => {
                        try {
                          const { data } = await axios.post(`${API}/admin/tax-report/backfill-stripe`, null, { params: { year: taxReport.year } });
                          const added = data.total_added ?? ((data.invoices_added || 0) + (data.subscriptions_added || 0) + (data.sessions_added || 0));
                          if (data.errors?.length > 0) {
                            toast.warning(`Backfill partial: +${added} added. Errors: ${data.errors.join(' | ')}`, { duration: 12000 });
                          } else if (added === 0) {
                            toast.info(`Backfill ran — no new entries to add (Stripe returned ${data.invoices_seen} invoices, ${data.subscriptions_seen} subs, ${data.sessions_seen} sessions).`);
                          } else {
                            toast.success(`Backfilled +${added} (${data.invoices_added || 0} invoices · ${data.subscriptions_added || 0} subs · ${data.sessions_added || 0} deposits)`);
                          }
                          loadTaxReport(taxReport.year);
                        } catch (e) {
                          toast.error(`Backfill failed: ${e.response?.data?.detail || e.message}`, { duration: 12000 });
                        }
                      }}
                    >
                      Backfill {taxReport.year}
                    </Button>
                  </CardContent>
                </Card>

                {/* Loyalty backfill — retroactive point award for completed trips */}
                <Card className="bg-[#121212] border-emerald-500/20" data-testid="loyalty-backfill-card">
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-emerald-300">Backfill loyalty points</p>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Grants the loyalty points every completed trip should have earned. Use this once after the 2026-05-25 fix — every prior trip that was missing points (because the new completion path wasn&apos;t wired) gets caught up. Active Link Pro members get 1.5×. Idempotent.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                      data-testid="loyalty-backfill-btn"
                      onClick={async () => {
                        if (!window.confirm(`Backfill loyalty points for every completed trip in ${taxReport.year}? Safe to re-run — same trip won't double-credit.`)) return;
                        try {
                          const { data } = await axios.post(`${API}/admin/loyalty/backfill`, null, { params: { year: taxReport.year } });
                          const added = data.awarded || 0;
                          const pts = data.points_distributed || 0;
                          const users = (data.per_user || []).length;
                          if (added === 0) {
                            toast.info(`No new points to award — scanned ${data.total_bookings_scanned} trips, ${data.already_awarded} already had points, ${data.no_customer_match} had no customer match.`, { duration: 10000 });
                          } else {
                            toast.success(`Awarded ${pts} points to ${users} customer${users === 1 ? '' : 's'} across ${added} trip${added === 1 ? '' : 's'}.`, { duration: 10000 });
                          }
                        } catch (e) {
                          toast.error(`Backfill failed: ${e.response?.data?.detail || e.message}`, { duration: 12000 });
                        }
                      }}
                    >
                      Award missing points
                    </Button>
                  </CardContent>
                </Card>

                {/* Attach orphan bookings — retroactive user_id link */}
                <Card className="bg-[#121212] border-[#4D88FF]/20" data-testid="attach-orphans-card">
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#4D88FF]">Attach orphan bookings</p>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Sweeps every booking without a linked customer account and tries to attach it by email or phone. Orphan trips become visible in the customer&apos;s My Trips and become eligible for the next loyalty backfill. Idempotent.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#4D88FF]/40 text-[#4D88FF] hover:bg-[#4D88FF]/10"
                      data-testid="attach-orphans-btn"
                      onClick={async () => {
                        if (!window.confirm("Sweep all orphan bookings and try to attach them to existing customers? Safe to re-run.")) return;
                        try {
                          const { data } = await axios.post(`${API}/admin/bookings/attach-orphans`);
                          if (data.attached === 0) {
                            toast.info(`Scanned ${data.examined} orphans — none matched an existing account.`, { duration: 10000 });
                          } else {
                            toast.success(`Attached ${data.attached} of ${data.examined} orphan booking${data.examined === 1 ? '' : 's'} to existing customers (${data.skipped_no_match} still unmatched).`, { duration: 10000 });
                          }
                        } catch (e) {
                          toast.error(`Attach failed: ${e.response?.data?.detail || e.message}`, { duration: 12000 });
                        }
                      }}
                    >
                      Attach orphans
                    </Button>
                  </CardContent>
                </Card>

                {/* One-tap relink + award — fixes customers who earned no points
                    because their booking never recorded a user_id (Christina-style).
                    Matches by last-10 phone digits, stamps user_id, then awards. */}
                <Card className="bg-[#121212] border-fuchsia-500/20" data-testid="catchup-loyalty-card">
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-fuchsia-300">Catch up loyalty points</p>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Combines orphan-attach + loyalty-backfill in one tap. Stamps
                        the customer&apos;s user_id onto any booking that&apos;s missing
                        it (phone-match), then retroactively awards loyalty points
                        for every completed trip. Use this when a customer says they
                        haven&apos;t been getting points. Idempotent.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                      data-testid="catchup-loyalty-btn"
                      onClick={async () => {
                        if (!window.confirm("Relink every orphan booking by phone and award the loyalty points that were missed? Safe to re-run.")) return;
                        try {
                          const { data } = await axios.post(`${API}/admin/diagnostics/relink-bookings-and-award-points`);
                          const relinked = data.bookings_relinked || 0;
                          const pts = data.total_points_distributed || 0;
                          const ppl = (data.per_customer || []).length;
                          if (relinked === 0 && pts === 0) {
                            toast.info(`Nothing to fix — every booking was already linked and points up to date.`, { duration: 8000 });
                          } else {
                            toast.success(`Relinked ${relinked} booking${relinked === 1 ? '' : 's'} · Awarded ${pts} points to ${ppl} customer${ppl === 1 ? '' : 's'}.`, { duration: 12000 });
                          }
                        } catch (e) {
                          toast.error(`Catch-up failed: ${e.response?.data?.detail || e.message}`, { duration: 12000 });
                        }
                      }}
                    >
                      Catch up points
                    </Button>
                  </CardContent>
                </Card>

                {/* Mercantile tax preview */}
                <Card className="bg-amber-500/5 border-amber-500/20" data-testid="mercantile-card">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-amber-300">Wyomissing Mercantile Tax (Service rate {taxReport.mercantile.rate_pct}%)</p>
                      <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Annual filing</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs text-amber-200/90">
                      <div>
                        <p className="text-[10px] uppercase text-amber-300/60">Taxable volume {taxReport.year}</p>
                        <p className="text-base font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>${taxReport.mercantile.taxable_volume.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-amber-300/60">Tax due (volume × {taxReport.mercantile.rate_decimal})</p>
                        <p className="text-base font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>${taxReport.mercantile.tax_due.toFixed(2)}</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-amber-300/60">
                      File with {taxReport.mercantile.filing_authority} by <strong>{taxReport.mercantile.deadline}</strong>
                    </p>
                  </CardContent>
                </Card>

                {/* Monthly breakdown */}
                <Card className="bg-[#121212] border-white/5">
                  <CardHeader>
                    <CardTitle className="text-base text-white">Monthly breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-zinc-500 border-b border-white/5">
                        <tr>
                          <th className="text-left p-2 font-medium">Month</th>
                          <th className="text-right p-2 font-medium">Trips</th>
                          <th className="text-right p-2 font-medium">Gross</th>
                          <th className="text-right p-2 font-medium">Sales Tax</th>
                          <th className="text-right p-2 font-medium">Driver Pay</th>
                          <th className="text-right p-2 font-medium">Platform</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(taxReport.monthly).map(([m, b]) => {
                          const monthName = new Date(taxReport.year, parseInt(m, 10) - 1, 1).toLocaleString('en-US', { month: 'short' });
                          return (
                            <tr key={m} className="border-b border-white/5 hover:bg-white/5">
                              <td className="p-2 text-zinc-300">{monthName}</td>
                              <td className="p-2 text-right text-zinc-400">{b.count}</td>
                              <td className="p-2 text-right text-white">${b.gross.toFixed(2)}</td>
                              <td className="p-2 text-right text-amber-400">${b.sales_tax.toFixed(2)}</td>
                              <td className="p-2 text-right text-[#4D88FF]">${b.driver.toFixed(2)}</td>
                              <td className="p-2 text-right text-emerald-400">${b.platform.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Per-driver 1099 cross-check */}
                <Card className="bg-[#121212] border-white/5">
                  <CardHeader>
                    <CardTitle className="text-base text-white flex items-center justify-between">
                      <span>Driver earnings — 1099-NEC cross-check</span>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">$600+ requires 1099</span>
                    </CardTitle>
                    <p className="text-[11px] text-zinc-500 mt-1">
                      Tips are broken out separately — they&apos;re taxable income to the driver but never sales-taxed at the platform.
                    </p>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    {taxReport.per_driver.length === 0 ? (
                      <p className="p-4 text-xs text-zinc-500">No completed driver trips this year yet.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="text-zinc-500 border-b border-white/5">
                          <tr>
                            <th className="text-left p-2 font-medium">Driver</th>
                            <th className="text-right p-2 font-medium">Trips</th>
                            <th className="text-right p-2 font-medium">Earnings</th>
                            <th className="text-right p-2 font-medium">Tips</th>
                            <th className="text-right p-2 font-medium">Total</th>
                            <th className="text-center p-2 font-medium">1099?</th>
                            <th className="text-center p-2 font-medium">Stripe Connect</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxReport.per_driver.map(d => (
                            <tr key={d.driver_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`tax-row-${d.driver_id}`}>
                              <td className="p-2 text-zinc-300">{d.name}</td>
                              <td className="p-2 text-right text-zinc-400">{d.trip_count}</td>
                              <td className="p-2 text-right text-[#4D88FF]">${d.earnings.toFixed(2)}</td>
                              <td className="p-2 text-right text-zinc-400">${d.tips.toFixed(2)}</td>
                              <td className="p-2 text-right text-white font-bold">${d.total_paid.toFixed(2)}</td>
                              <td className="p-2 text-center">
                                {d.needs_1099
                                  ? <span className="text-[10px] uppercase font-bold text-red-400">Required</span>
                                  : <span className="text-[10px] uppercase font-bold text-zinc-600">—</span>}
                              </td>
                              <td className="p-2 text-center">
                                {d.stripe_connect
                                  ? <span className="text-emerald-400">✓ Auto</span>
                                  : <span className="text-amber-400">⚠ Manual</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Heatmap Tab */}
          <TabsContent value="heatmap" className="space-y-4" data-testid="heatmap-tab-content">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm text-zinc-300 font-medium">Pickup density — last {heatmap?.days || heatmapDays} days</p>
                <p className="text-xs text-zinc-500 mt-0.5">{heatmap?.total_pickups_with_coords ?? 0} bookings with GPS coordinates · clustered to ~0.7 mi</p>
              </div>
              <div className="flex gap-2">
                {[7, 30, 90, 365].map(d => (
                  <button
                    key={d}
                    onClick={() => setHeatmapDays(d)}
                    data-testid={`heatmap-days-${d}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      heatmapDays === d ? "bg-[#0047FF] text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    {d === 365 ? "1y" : `${d}d`}
                  </button>
                ))}
              </div>
            </div>

            {(!heatmap || heatmap.points.length === 0) ? (
              <Card className="bg-[#121212] border-white/5">
                <CardContent className="p-12 text-center">
                  <MapPin className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">No bookings with GPS coordinates in this window</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Top zones table */}
                <Card className="bg-[#121212] border-white/5">
                  <CardHeader>
                    <CardTitle className="text-base font-medium text-white">Top pickup zones</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-900/50 text-zinc-400 uppercase tracking-wider">
                        <tr>
                          <th className="text-left p-2 font-medium">Location</th>
                          <th className="text-right p-2 font-medium">Pickups</th>
                          <th className="text-right p-2 font-medium">Completed</th>
                          <th className="text-right p-2 font-medium">Cancelled</th>
                          <th className="text-right p-2 font-medium">Revenue</th>
                          <th className="text-center p-2 font-medium">Map</th>
                        </tr>
                      </thead>
                      <tbody>
                        {heatmap.points.slice(0, 25).map((p, i) => {
                          const intensity = Math.min(1, p.count / Math.max(1, heatmap.points[0].count));
                          const fallback = `${p.lat.toFixed(2)}, ${p.lng.toFixed(2)}`;
                          const topAddrs = Array.isArray(p.top_addresses) ? p.top_addresses : [];
                          return (
                            <tr key={i} className="border-t border-white/5 align-top">
                              <td className="p-2 text-zinc-200">
                                <div className="flex items-start gap-2">
                                  <span
                                    className="inline-block w-2.5 h-2.5 rounded-full mt-1 shrink-0"
                                    style={{ background: `rgba(255, 99, 71, ${0.25 + intensity * 0.75})` }}
                                  />
                                  <div className="min-w-0">
                                    <div className="font-medium" data-testid={`heatmap-row-label-${i}`}>{p.label || fallback}</div>
                                    {topAddrs.length > 0 && (
                                      <details className="mt-0.5 text-zinc-400">
                                        <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300 select-none">
                                          Top {topAddrs.length} address{topAddrs.length === 1 ? '' : 'es'}
                                        </summary>
                                        <ul className="mt-1 space-y-0.5">
                                          {topAddrs.map((a, ai) => (
                                            <li key={ai} className="text-[11px] text-zinc-300 truncate" title={a.address}>
                                              <span className="text-zinc-500 mr-1">{a.count}×</span>{a.address}
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="p-2 text-right text-white font-bold">{p.count}</td>
                              <td className="p-2 text-right text-emerald-400">{p.completed}</td>
                              <td className="p-2 text-right text-amber-400">{p.cancelled}</td>
                              <td className="p-2 text-right text-zinc-300">${p.revenue.toFixed(2)}</td>
                              <td className="p-2 text-center">
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#4D88FF] hover:underline"
                                >
                                  Open
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo" className="space-y-4" data-testid="promo-tab-content">
            <Card className="bg-[#121212] border-white/5">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Create / update promo code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-zinc-400">Code</Label>
                    <Input value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} placeholder="LAUNCH50" className="uppercase tracking-wider bg-black/30 border-white/10" data-testid="promo-form-code" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Discount %</Label>
                    <Input type="number" min="0" max="100" step="1" value={promoForm.discount_pct} onChange={(e) => setPromoForm({ ...promoForm, discount_pct: e.target.value, discount_cents: "" })} placeholder="50" className="bg-black/30 border-white/10" data-testid="promo-form-pct" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">…or flat $ off</Label>
                    <Input type="number" min="0" step="0.01" value={promoForm.discount_cents} onChange={(e) => setPromoForm({ ...promoForm, discount_cents: e.target.value, discount_pct: "" })} placeholder="5.00" className="bg-black/30 border-white/10" data-testid="promo-form-flat" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Max uses (blank = ∞)</Label>
                    <Input type="number" min="1" step="1" value={promoForm.max_uses} onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })} placeholder="1000" className="bg-black/30 border-white/10" data-testid="promo-form-max" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Expires (ISO date or blank)</Label>
                    <Input type="datetime-local" value={promoForm.expires_at} onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-black/30 border-white/10" data-testid="promo-form-expires" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Description</Label>
                    <Input value={promoForm.description} onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })} placeholder="Launch promo" className="bg-black/30 border-white/10" data-testid="promo-form-desc" />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 text-xs text-zinc-300">
                    <input type="checkbox" checked={!!promoForm.active} onChange={(e) => setPromoForm({ ...promoForm, active: e.target.checked })} data-testid="promo-form-active" />
                    Active
                  </label>
                  <Button onClick={savePromoCode} disabled={promoSaving} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="promo-save-button">
                    {promoSaving ? "Saving…" : "Save / update code"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/5">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-white">All promo codes ({promoCodes.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="promo-list">
                {promoCodes.length === 0 ? (
                  <p className="text-sm text-zinc-500">No promo codes yet. Create one above to start.</p>
                ) : promoCodes.map(p => (
                  <div key={p.code} className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5" data-testid={`promo-row-${p.code}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-white">{p.code}</span>
                        <span className="text-xs text-emerald-400 font-bold">
                          {p.discount_pct ? `${p.discount_pct}% off` : p.discount_cents ? `$${(p.discount_cents / 100).toFixed(2)} off` : ""}
                        </span>
                        {!p.active && <Badge variant="outline" className="text-xs text-zinc-400 border-zinc-700">Inactive</Badge>}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {p.used_count || 0}/{p.max_uses ?? "∞"} used
                        {p.expires_at ? ` · expires ${new Date(p.expires_at).toLocaleDateString()}` : ""}
                        {p.description ? ` · ${p.description}` : ""}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deletePromoCode(p.code)} className="text-red-400 hover:text-red-300" data-testid={`promo-delete-${p.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webhook Events Tab */}
          <TabsContent value="webhooks" className="space-y-4" data-testid="webhooks-tab-content">
            {/* Owner alerts — surface payment failures + subscription cancels from last 7 days */}
            {(() => {
              const ALERT_TYPES = new Set(["invoice.payment_failed", "customer.subscription.deleted"]);
              const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
              const alerts = webhookEvents.filter((ev) => {
                if (!ALERT_TYPES.has(ev.type)) return false;
                const ts = ev.received_at ? new Date(ev.received_at).getTime() : 0;
                return ts >= sevenDaysAgo;
              });
              if (alerts.length === 0) return null;
              const failedCount = alerts.filter((a) => a.type === "invoice.payment_failed").length;
              const cancelCount = alerts.filter((a) => a.type === "customer.subscription.deleted").length;
              return (
                <Card
                  className="bg-gradient-to-br from-red-500/15 via-[#0a0a0a] to-[#0a0a0a] border-red-500/40"
                  data-testid="webhook-owner-alerts"
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-bold text-red-300 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      {alerts.length} owner alert{alerts.length === 1 ? '' : 's'} in the last 7 days
                    </CardTitle>
                    <p className="text-xs text-zinc-400 mt-1">
                      {failedCount > 0 && (
                        <>
                          <span className="text-red-300 font-semibold">{failedCount}</span> failed payment{failedCount === 1 ? '' : 's'}
                        </>
                      )}
                      {failedCount > 0 && cancelCount > 0 && " · "}
                      {cancelCount > 0 && (
                        <>
                          <span className="text-red-300 font-semibold">{cancelCount}</span> subscription cancel{cancelCount === 1 ? '' : 's'}
                        </>
                      )}
                      {" — review below and reach out before churn sticks."}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {alerts.slice(0, 10).map((ev) => (
                      <div
                        key={ev.id}
                        className="p-3 rounded-lg bg-red-500/5 border border-red-500/20"
                        data-testid={`webhook-alert-${ev.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-mono text-xs text-red-200 truncate">
                              {ev.type === "invoice.payment_failed" ? "Payment failed" : "Subscription cancelled"}
                            </p>
                            <p className="text-[11px] text-zinc-400 mt-0.5">
                              {new Date(ev.received_at).toLocaleString()}
                              {ev.amount_total ? ` · $${(ev.amount_total / 100).toFixed(2)}` : ""}
                              {ev.customer_id ? ` · ${ev.customer_id}` : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {alerts.length > 10 && (
                      <p className="text-xs text-zinc-500 pt-1">
                        Plus {alerts.length - 10} more — scroll the full event list below.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            <Card className="bg-[#121212] border-white/5">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                  <Radio className="w-4 h-4" /> Recent Stripe webhook events
                </CardTitle>
                <Button size="sm" variant="outline" onClick={loadWebhookEvents} className="border-white/10 text-zinc-300 hover:bg-white/5" data-testid="webhooks-refresh">
                  Refresh
                </Button>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="webhook-events-list">
                {webhookEvents.length === 0 ? (
                  <p className="text-sm text-zinc-500">No webhook events recorded yet. They'll appear here as Stripe fires them.</p>
                ) : webhookEvents.map(ev => (
                  <div key={ev.id} className="p-3 rounded-lg bg-black/30 border border-white/5" data-testid={`webhook-event-${ev.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-white truncate">{ev.type}</p>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          {new Date(ev.received_at).toLocaleString()}
                          {ev.mode ? ` · ${ev.mode}` : ""}
                          {ev.amount_total ? ` · $${(ev.amount_total / 100).toFixed(2)}` : ""}
                          {ev.status ? ` · ${ev.status}` : ""}
                        </p>
                      </div>
                      <Badge variant="outline" className={ev.signature_valid ? "text-emerald-400 border-emerald-500/30" : "text-red-400 border-red-500/30"}>
                        {ev.signature_valid ? "verified" : "unsigned"}
                      </Badge>
                    </div>
                    {(ev.customer_id || ev.subscription_id) && (
                      <p className="text-[10px] font-mono text-zinc-600 mt-1 truncate">
                        {ev.customer_id ? `cust:${ev.customer_id}` : ""}
                        {ev.customer_id && ev.subscription_id ? " · " : ""}
                        {ev.subscription_id ? `sub:${ev.subscription_id}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="franchise" className="space-y-4" data-testid="franchise-tab-content">
            {/* Owner-only inbox for franchise-source tickets. Empty state
                covers the "no tickets yet" case gracefully. */}
            <FranchiseTicketsInbox />

            {/* Simple ticket system — anyone viewing this tab (platform
                owner or a tenant admin) can file an issue to Link Ride
                support. Emails the owner immediately and lands in the
                existing support inbox (source='franchise'). */}
            <Card className="bg-[#121212] border-white/5" data-testid="franchise-support-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#4D88FF]" />
                  Contact Link Ride support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-zinc-400">
                  Something broken? App update request? Billing question? File a ticket and Link Ride Ops replies by email.
                </p>
                <SupportTicketDialog
                  source="franchise"
                  triggerLabel="Open a ticket"
                  triggerClassName="inline-flex items-center gap-2 bg-[#0047FF] hover:bg-[#003BE0] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors"
                  testIdPrefix="franchise-support"
                />
              </CardContent>
            </Card>

            <FranchiseAdminPanel token={typeof window !== 'undefined' ? localStorage.getItem('linklite_token') : ''} />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <ChangePasswordCard />
            <BulkPasteStripeCard />
            <Card className="bg-[#121212] border-white/5" data-testid="signup-caps-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <Percent className="w-4 h-4 text-amber-400" />
                  Beta Capacity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  Hard caps that block new signups + Link Pro subs once full. Override per-env via
                  {" "}<code className="text-zinc-400">Link Ride_CUSTOMER_SIGNUP_CAP</code> and
                  {" "}<code className="text-zinc-400">Link Ride_PRO_SUBSCRIBER_CAP</code>. Set to 0 to disable.
                </p>
                <CapMeter label="Customer accounts" snapshot={signupCaps?.customers} testid="cap-customers" />
                <CapMeter label="Link Pro members" snapshot={signupCaps?.link_pro} testid="cap-link-pro" />

                {Array.isArray(signupCaps?.link_pro?.members) && signupCaps.link_pro.members.length > 0 && (
                  <details className="mt-1 group" data-testid="link-pro-members-detail">
                    <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300 select-none">
                      Show {signupCaps.link_pro.members.length} active Link Pro {signupCaps.link_pro.members.length === 1 ? "member" : "members"}
                    </summary>
                    <ul className="mt-2 space-y-1.5">
                      {signupCaps.link_pro.members.map((m) => (
                        <li key={m.id} className="text-[12px] text-zinc-300 flex items-center justify-between gap-2 py-1 border-b border-white/5">
                          <div className="min-w-0">
                            <div className="truncate font-medium">{m.name}</div>
                            <div className="truncate text-zinc-500 text-[11px]">{m.email}</div>
                          </div>
                          <div className="text-right shrink-0 text-[11px]">
                            {m.comp && <span className="text-amber-300 mr-2">comp</span>}
                            {m.expires_at && (
                              <span className="text-zinc-500">until {String(m.expires_at).slice(0, 10)}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/5" data-testid="feature-catalog-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-400" />
                  Feature Catalog
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-zinc-400 leading-relaxed">
                  One-click PDF inventory of every feature in Link Ride — customer, driver, admin, automation, and integrations. Hand to investors, new drivers, or your accountant. Regenerated on demand so it&apos;s always current with the live code.
                </p>
                <Button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/features.pdf`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      const stamp = new Date().toISOString().slice(0, 10);
                      a.download = `Link Ride-feature-catalog-${stamp}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success("Feature catalog downloaded");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Download failed");
                    }
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-xl"
                  data-testid="download-feature-catalog"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/5">
              <CardHeader>
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#4D88FF]" />
                  Support Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-400 text-sm">Support Phone Number</Label>
                  <Input
                    value={config.supportPhone || ""}
                    onChange={(e) => setConfig(prev => ({ ...prev, supportPhone: e.target.value }))}
                    placeholder="1 (484) 577-3677"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/5" data-testid="dispatch-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#4D88FF]" />
                  Job Dispatch
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-zinc-500">
                  Choose how new bookings are offered to drivers.
                </p>

                <div className="grid grid-cols-1 gap-2">
                  {[
                    {
                      id: "manual",
                      title: "Manual broadcast",
                      desc: "Every approved driver gets pinged at once. First to claim wins.",
                    },
                    {
                      id: "auto_time",
                      title: "Automated · by time",
                      desc: "Offer goes to one driver at a time. After the timeout, it passes to the next.",
                    },
                    {
                      id: "auto_distance",
                      title: "Automated · by distance",
                      desc: "Offers go to the closest driver first. Same per-driver timeout.",
                    },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setConfig(prev => ({ ...prev, dispatchMode: opt.id }))}
                      data-testid={`dispatch-mode-${opt.id}`}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        config.dispatchMode === opt.id
                          ? "bg-[#0047FF]/10 border-[#0047FF]/40"
                          : "bg-zinc-800 border-white/5 hover:border-white/15"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-semibold ${config.dispatchMode === opt.id ? "text-white" : "text-zinc-200"}`}>
                          {opt.title}
                        </p>
                        {config.dispatchMode === opt.id && <CheckCircle className="w-4 h-4 text-[#4D88FF]" />}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {(config.dispatchMode === "auto_time" || config.dispatchMode === "auto_distance") && (
                  <div>
                    <Label className="text-zinc-400 text-sm">Time per driver (seconds)</Label>
                    <Input
                      type="number"
                      min="5"
                      max="300"
                      step="5"
                      value={config.dispatchWindowSeconds}
                      onChange={(e) => setConfig(prev => ({ ...prev, dispatchWindowSeconds: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="dispatch-window-input"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">
                      Each driver has this long to accept the offer before it rolls to the next.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/5" data-testid="tax-config-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <FileImage className="w-4 h-4 text-amber-400" />
                  Taxes & Commission
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-zinc-500">
                  These rates affect every new quote and the Tax & Reporting tab.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-sm">PA Sales Tax %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={config.salesTaxPct}
                      onChange={(e) => setConfig(prev => ({ ...prev, salesTaxPct: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-sales-tax"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Added to every customer fare</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Business Name</Label>
                    <Input
                      value={config.businessName || ""}
                      onChange={(e) => setConfig(prev => ({ ...prev, businessName: e.target.value }))}
                      placeholder="Link Ride LLC"
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-business-name"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Shows on 1099s, receipts & tax exports</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Business EIN</Label>
                    <Input
                      value={config.businessEIN || ""}
                      onChange={(e) => setConfig(prev => ({ ...prev, businessEIN: e.target.value }))}
                      placeholder="12-3456789"
                      className="bg-zinc-800 border-white/10 text-white mt-1 font-mono"
                      data-testid="config-business-ein"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Federal tax ID for 1099 filings</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Mercantile Tax %</Label>
                    <Input
                      type="number"
                      step="0.001"
                      min="0"
                      max="5"
                      value={config.mercantileTaxPct}
                      onChange={(e) => setConfig(prev => ({ ...prev, mercantileTaxPct: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-mercantile"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Wyomissing annual filing rate</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Platform Commission %</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={config.platformCommissionPct}
                      onChange={(e) => setConfig(prev => ({ ...prev, platformCommissionPct: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-commission"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Drivers get the remaining {100 - (Number(config.platformCommissionPct) || 0)}%</p>
                  </div>
                </div>

                <div className="pt-3 border-t border-white/5">
                  <Label className="text-zinc-400 text-sm">IRS Standard Mileage Rate ($/mile)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    max="5"
                    value={config.irsStandardMileageRate ?? 0.70}
                    onChange={(e) => setConfig(prev => ({ ...prev, irsStandardMileageRate: e.target.value }))}
                    className="bg-zinc-800 border-white/10 text-white mt-1 max-w-xs"
                    data-testid="config-irs-mileage"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">
                    Used by driver Tax Center for Standard Mileage deduction. <strong>2025 = $0.70</strong>. Update each January when IRS announces the new rate.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/5" data-testid="geo-anchor-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#4D88FF]" />
                  Address Search Bias
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Anchor address suggestions to your operating hub. Riders who haven't allowed GPS
                  still see <strong>local results first</strong> when typing partial addresses
                  (e.g. "100 Main St").
                </p>

                {/* Quick address-to-coords lookup */}
                <div className="rounded-lg bg-zinc-800/50 border border-white/5 p-3 space-y-2">
                  <Label className="text-zinc-400 text-xs">Quick set from address / zip</Label>
                  <div className="flex gap-2">
                    <Input
                      id="geo-anchor-lookup-input"
                      placeholder="West Reading, PA 19611"
                      className="bg-zinc-800 border-white/10 text-white text-sm"
                      data-testid="config-anchor-lookup-input"
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          document.getElementById('geo-anchor-lookup-btn')?.click();
                        }
                      }}
                    />
                    <Button
                      id="geo-anchor-lookup-btn"
                      type="button"
                      variant="outline"
                      className="bg-zinc-800 border-white/15 text-white hover:bg-zinc-700 whitespace-nowrap"
                      data-testid="config-anchor-lookup-btn"
                      onClick={async () => {
                        const q = document.getElementById('geo-anchor-lookup-input')?.value || '';
                        if (!q.trim()) {
                          toast.error("Type an address or zip first");
                          return;
                        }
                        const res = await geocodeAddress(q);
                        if (!res) {
                          toast.error("Couldn't find that address");
                          return;
                        }
                        setConfig(prev => ({
                          ...prev,
                          geoAnchorLat: Number(res.lat.toFixed(4)),
                          geoAnchorLng: Number(res.lng.toFixed(4)),
                        }));
                        toast.success(`Anchor set: ${res.display.split(',').slice(0, 2).join(',')}`);
                      }}
                    >
                      Lookup
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-sm">Anchor Latitude</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={config.geoAnchorLat}
                      onChange={(e) => setConfig(prev => ({ ...prev, geoAnchorLat: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-anchor-lat"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Anchor Longitude</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={config.geoAnchorLng}
                      onChange={(e) => setConfig(prev => ({ ...prev, geoAnchorLng: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-anchor-lng"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Bias Radius (miles)</Label>
                    <Input
                      type="number"
                      step="5"
                      min="10"
                      max="200"
                      value={config.geoAutocompleteBiasMiles}
                      onChange={(e) => setConfig(prev => ({ ...prev, geoAutocompleteBiasMiles: e.target.value }))}
                      className="bg-zinc-800 border-white/10 text-white mt-1"
                      data-testid="config-anchor-miles"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">~60 mi is balanced</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border border-amber-500/20" data-testid="tax-reminders-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-amber-300 flex items-center gap-2">
                  Tax-deadline reminders (Resend email)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Automatic emails to <code className="text-amber-300">OWNER_NOTIFICATION_EMAIL</code> at <strong>T-30</strong>, <strong>T-7</strong>, and <strong>T-1</strong> days before each PA / IRS / West Reading / Wyomissing filing deadline. Background task runs every 6 hours. Idempotent — duplicates impossible.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/admin/tax-reminders/check`);
                        toast.success(`Checked ${data.filings_checked} filings · sent ${data.sent} · skipped ${data.skipped_already_sent} already sent`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Check failed");
                      }
                    }}
                    variant="outline"
                    className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                    data-testid="tax-reminders-check-btn"
                  >
                    Run check now
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        const { data } = await axios.get(`${API}/admin/tax-reminders/log`);
                        if (!data.count) {
                          toast.info("No reminders sent yet — none are due in the current window.");
                        } else {
                          toast.success(`${data.count} reminder(s) sent · most recent: ${data.log[0].filing_key} due ${data.log[0].due_date}`);
                        }
                      } catch (e) {
                        toast.error("Could not load log");
                      }
                    }}
                    variant="ghost"
                    className="text-zinc-300 hover:bg-zinc-800"
                    data-testid="tax-reminders-log-btn"
                  >
                    View log
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border border-[#0047FF]/20" data-testid="sentdm-test-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-[#80B0FF] flex items-center gap-2">
                  Owner SMS alerts (SentDM)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Every new booking sends an SMS to <code className="text-[#80B0FF]">OWNER_PHONE</code> in
                  addition to the existing email alert. Requires a template UUID from your{" "}
                  <a href="https://app.sent.dm" target="_blank" rel="noreferrer" className="text-[#4D88FF] underline">SentDM dashboard</a>{" "}
                  pasted into <code className="text-[#80B0FF]">SENT_DM_OWNER_TEMPLATE_ID</code>.
                </p>
                <Button
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API}/admin/sentdm/test`);
                      if (data?.ok) {
                        toast.success(`Test SMS sent! Message ID: ${data.message_id?.slice(0, 12) || 'n/a'}`);
                      } else {
                        toast.warning(data?.detail || "Test SMS not sent — see server logs");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Test SMS request failed");
                    }
                  }}
                  disabled={saving}
                  variant="outline"
                  className="border-[#0047FF]/40 text-[#80B0FF] hover:bg-[#0047FF]/10 justify-start"
                  data-testid="sentdm-test-button"
                >
                  Send test SMS to owner
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border border-red-500/15" data-testid="maintenance-card">
              <CardHeader>
                <CardTitle className="text-base font-medium text-red-300 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Maintenance — Bulk delete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-zinc-500">
                  Use these to clean up before going live with real customers, or to clear ghost bookings. <strong className="text-red-300">There is no undo.</strong>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button onClick={wipeTestBookings} disabled={saving} variant="outline" className="border-red-500/25 text-red-300 hover:bg-red-500/10 justify-start" data-testid="wipe-test-bookings">
                    <Trash2 className="w-3.5 h-3.5 mr-2" />Delete test-data bookings
                  </Button>
                  <Button onClick={() => wipeBookings("pending")} disabled={saving} variant="outline" className="border-red-500/25 text-red-300 hover:bg-red-500/10 justify-start" data-testid="wipe-pending">
                    <Trash2 className="w-3.5 h-3.5 mr-2" />Delete all PENDING
                  </Button>
                  <Button onClick={() => wipeBookings("cancelled")} disabled={saving} variant="outline" className="border-red-500/25 text-red-300 hover:bg-red-500/10 justify-start" data-testid="wipe-cancelled">
                    <Trash2 className="w-3.5 h-3.5 mr-2" />Delete all CANCELLED
                  </Button>
                  <Button onClick={() => wipeBookings("no_show")} disabled={saving} variant="outline" className="border-red-500/25 text-red-300 hover:bg-red-500/10 justify-start" data-testid="wipe-noshow">
                    <Trash2 className="w-3.5 h-3.5 mr-2" />Delete all NO-SHOW
                  </Button>
                  <Button onClick={wipeLegacyJobs} disabled={saving} variant="outline" className="border-red-500/25 text-red-300 hover:bg-red-500/10 justify-start col-span-full h-auto py-2.5 whitespace-normal text-left" data-testid="wipe-legacy-jobs">
                    <Trash2 className="w-3.5 h-3.5 mr-2 flex-shrink-0" />Delete legacy demo "seed" jobs (multi-stop fake deliveries)
                  </Button>
                  <Button onClick={wipeAllExceptCustomer} disabled={saving} variant="outline" className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 justify-start col-span-full h-auto py-2.5 whitespace-normal text-left" data-testid="wipe-except-customer">
                    <Trash2 className="w-3.5 h-3.5 mr-2 flex-shrink-0" />Delete ALL bookings except one customer
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/5">
              <CardHeader>
                <CardTitle className="text-base font-medium text-white">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start border-white/10 text-zinc-300 hover:bg-zinc-800" onClick={() => navigate("/driver/schedule")}>
                  Open Calendar & Time Off
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-zinc-300 hover:bg-zinc-800" onClick={() => navigate("/driver/bookings")}>
                  View Bookings
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-zinc-300 hover:bg-zinc-800" onClick={() => window.open("/book", "_blank")}>
                  Customer Booking Page
                </Button>
              </CardContent>
            </Card>

            <Button onClick={saveConfig} disabled={saving} className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0">
              <Save className="w-5 h-5 mr-2" />{saving ? "Saving..." : "Save Settings"}
            </Button>
          </TabsContent>
        </Tabs>
      </main>
      <DriverBottomNav />

      {/* Driver Rejection Dialog — captures a required reason and stores it on
          the user doc so the applicant has a record of why they were turned down. */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open && !rejectSubmitting) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Reject driver application</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {rejectTarget ? `Why are you rejecting ${rejectTarget.name}?` : ""} The reason is saved to their record so you have a paper trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5" data-testid="reject-presets">
              {REJECTION_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setRejectReason(preset === "Other (see notes)" ? "" : preset)}
                  className="text-xs px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10 hover:border-white/20"
                  data-testid={`reject-preset-${preset.split(" ")[0].toLowerCase()}`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <div>
              <Label htmlFor="reject-reason" className="text-xs uppercase tracking-wide text-zinc-400">Reason (required)</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter the specific reason this driver was rejected..."
                rows={4}
                className="mt-1 bg-black/40 border-white/10 text-white placeholder:text-zinc-500"
                data-testid="reject-reason-input"
                autoFocus
              />
              <p className="text-[11px] text-zinc-500 mt-1">{rejectReason.trim().length} / 5 minimum characters</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setRejectTarget(null); setRejectReason(""); }}
              disabled={rejectSubmitting}
              className="border-white/15 text-zinc-300 hover:bg-white/5"
              data-testid="reject-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitReject}
              disabled={rejectSubmitting || rejectReason.trim().length < 5}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="reject-confirm-button"
            >
              {rejectSubmitting ? "Rejecting..." : "Confirm rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteCustomerDialog
        user={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => fetchCustomers?.()}
      />
    </div>
  );
}


/**
 * Tiny progress meter for the Beta capacity gauges on the Settings tab.
 * Renders a label, `used / cap`, and a horizontal fill bar that switches
 * color (green → amber → red) as we approach full.
 */
function CapMeter({ label, snapshot, testid }) {
  if (!snapshot) {
    return (
      <div className="space-y-1.5" data-testid={`${testid}-loading`}>
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-zinc-300 font-medium">{label}</span>
          <span className="text-xs text-zinc-600">loading…</span>
        </div>
        <div className="h-2 rounded-full bg-zinc-800/60" />
      </div>
    );
  }
  const used = snapshot.used ?? 0;
  const cap = snapshot.cap ?? 0;
  const disabled = !cap || cap <= 0;
  const pct = disabled ? 0 : Math.min(100, Math.round((used / cap) * 100));
  const tone =
    disabled ? "bg-zinc-600" :
    pct >= 100 ? "bg-rose-500" :
    pct >= 80  ? "bg-amber-400" :
                 "bg-emerald-400";
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-zinc-300 font-medium">{label}</span>
        <span className="text-xs font-mono text-zinc-400">
          {disabled ? <span className="text-zinc-500">cap disabled</span> :
            <><span className={pct >= 100 ? "text-rose-300 font-bold" : "text-white font-bold"}>{used}</span><span className="text-zinc-600"> / {cap}</span></>
          }
        </span>
      </div>
      <div className="h-2 rounded-full bg-zinc-800/60 overflow-hidden">
        <div
          className={`h-full ${tone} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {pct >= 100 && !disabled && (
        <p className="text-[10px] text-rose-300 mt-1" data-testid={`${testid}-full`}>
          Cap reached — new signups for this surface are blocked with a 503 and a waitlist note.
        </p>
      )}
    </div>
  );
}

